// DAEMON — run telemetry.
//
// Every number in this game was chosen by arithmetic, not by watching
// anyone play. This module exists to replace that arithmetic with
// evidence: which card kills people, whether grazing pays enough to
// afford a SIGKILL, whether runlevel 3 is a wall, which upgrades nobody
// ever takes.
//
// Three rules, and they are not negotiable:
//
//   1. **Aggregate only.** Umami is a counter, not a database. Nothing
//      here identifies a person, and nothing here is a raw value that
//      could become one. Times and counts are bucketed at the source.
//   2. **Low cardinality.** A distinct value per player is a broken
//      dashboard. Every field below has a small, fixed domain.
//   3. **Never per-tick.** Grazes fire dozens of times a second. This
//      module summarises a run; it does not narrate one.
//
// The sim cannot see any of this. Telemetry reads state, never writes
// it, and calls nothing the simulation depends on — a run plays
// identically with tracking blocked, which is also the only honest way
// to ship it.

import { track } from "../umami";
import { qualifyingCategories, resultFromState } from "./records";
import { SIGNALS } from "./sim";
import { buildArchetypes, buildSignature } from "./upgrades";
import { TICK_HZ, type SimEvent, type SimState } from "./types";

/** Coarse duration buckets. Seconds are the unit a human tunes in, and
 *  a bucket is what keeps the dashboard readable. */
export function bucketSeconds(ticks: number): string {
  const s = ticks / TICK_HZ;
  if (s < 10) return "<10s";
  if (s < 30) return "10-30s";
  if (s < 60) return "30-60s";
  if (s < 120) return "1-2m";
  if (s < 240) return "2-4m";
  return "4m+";
}

/** Buckets for counts that can run into the hundreds. */
export function bucketCount(n: number): string {
  if (n <= 0) return "0";
  if (n < 5) return "1-4";
  if (n < 15) return "5-14";
  if (n < 40) return "15-39";
  if (n < 100) return "40-99";
  return "100+";
}

/** Proportions, bucketed. A percentage is a raw value with two decimal
 *  places of entropy in it; a band is the thing you actually tune on.
 *  `n/a` rather than a divide-by-zero, because "they earned nothing" is
 *  a real and interesting answer, not a missing one. */
export function bucketRatio(part: number, whole: number): string {
  if (whole <= 0) return "n/a";
  const pct = (part / whole) * 100;
  if (pct <= 0) return "0%";
  if (pct < 25) return "1-24%";
  if (pct < 50) return "25-49%";
  if (pct < 75) return "50-74%";
  if (pct < 100) return "75-99%";
  return "100%";
}

/** Consecutive-day buckets. Retention is the only thing a daily is for,
 *  and the shape that matters is "did anyone come back at all", not
 *  whether a particular person is on day 23. */
export function bucketStreak(days: number): string {
  if (days <= 0) return "0";
  if (days === 1) return "1";
  if (days < 4) return "2-3";
  if (days < 7) return "4-6";
  if (days < 14) return "7-13";
  if (days < 30) return "14-29";
  return "30+";
}

/** Runs within one page session. The "one more go" measure, and the
 *  reason the domain stops at 20+: past that it is a habit, not a
 *  session, and the extra resolution buys nothing. */
export function bucketRuns(n: number): string {
  if (n <= 1) return "1";
  if (n === 2) return "2";
  if (n < 5) return "3-4";
  if (n < 10) return "5-9";
  if (n < 20) return "10-19";
  return "20+";
}

/** How the run ended. `quit` is the one nobody reports voluntarily and
 *  the one that matters most — it is the shape of losing interest. */
export type RunOutcome = "won" | "lost" | "quit";

const phaseName = (i: number): string => `rl${Math.max(1, 5 - i)}`;

export type RunSummary = {
  outcome: RunOutcome;
  nice: number;
  daily: boolean;
  ticks: number;
  phaseIndex: number;
  cardId: string | null;
  grazes: number;
  hitsTaken: number;
  cardsCleared: number;
  signalsFired: number;
  upgrades: string[];
  /** Ticks until the player was first hit, or null if never. */
  firstHitTick: number | null;
};

/** The payload actually sent. Split out so it can be asserted in tests
 *  without a browser, and so the privacy note in Settings can be
 *  checked against something real. */
export function runPayload(s: RunSummary): Record<string, string | number | boolean> {
  return {
    outcome: s.outcome,
    // The difficulty is the axis every other number has to be read
    // against, so it rides on every event.
    nice: s.nice,
    daily: s.daily,
    duration: bucketSeconds(s.ticks),
    // Where the run ended. The funnel that answers "is rl3 a wall?".
    phase: phaseName(s.phaseIndex),
    // Which named attack was on screen. The single most useful field
    // for deciding whether a pattern is unfair.
    card: s.cardId ?? "none",
    grazes: bucketCount(s.grazes),
    hits: bucketCount(s.hitsTaken),
    cardsCleared: s.cardsCleared,
    signals: bucketCount(s.signalsFired),
    upgrades: s.upgrades.length,
    // The build itself, on the run event, because this is the only
    // event that knows how the run *ended*. The draft event records
    // what was picked and can tell you a pick rate; it cannot tell you
    // whether the pick was any good. Correlating build against outcome
    // needs both on one row, so here they are.
    //
    // Sorted, so a build is one row rather than up to 24 permutations
    // of itself. There is a real fingerprinting cost to shipping four
    // ids alongside difficulty and duration — a four-card hand is one
    // of ~20k — and it is accepted deliberately: the analytics are
    // self-hosted, honour Do Not Track, carry no identifier to join
    // against, and the whole point of the pool is to be tuned.
    build: buildSignature(s.upgrades),
    // The coarse shape, which is what most tuning questions actually
    // ask. Useful at a fraction of the traffic the full signature needs.
    buildArch: buildArchetypes(s.upgrades),
    firstHit: s.firstHitTick === null ? "never" : bucketSeconds(s.firstHitTick),
  };
}

// ── Session shape ─────────────────────────────────────────────────────
//
// Module-level and deliberately so: a session is the page, not the run.
// Nothing here is persisted — a reload is a new session, which is the
// honest reading of "one more go" anyway.

let sessionRuns = 0;
let sessionBucket = "";

/** Reset the session counter. Exported for tests; production never
 *  calls it, because a page load already is the reset. */
export function resetSession(): void {
  sessionRuns = 0;
  sessionBucket = "";
}

/**
 * Count a run against the session and report only when the bucket
 * moves.
 *
 * A funnel, not a histogram: "how many sessions reached five runs" is
 * answerable by counting `runs=5-9` rows, and every session that got
 * there also emitted `2` and `3-4` on the way. The first run emits
 * nothing at all — `daemon_start` already counts it, and firing both
 * would just double the denominator of every rate on the dashboard.
 */
function countSessionRun(): void {
  sessionRuns += 1;
  const b = bucketRuns(sessionRuns);
  if (sessionRuns < 2 || b === sessionBucket) {
    sessionBucket = b;
    return;
  }
  sessionBucket = b;
  track("daemon_session", { runs: b });
}

// ── Economy ───────────────────────────────────────────────────────────

/** SIGKILL is the affordability question the economy turns on: it is
 *  the expensive verb, and if nobody can ever pay for it the graze
 *  reward is wrong. Read from the table rather than restated, so a
 *  retune of the cost cannot silently desync the metric from the game. */
const SIGKILL_INDEX = SIGNALS.findIndex((s) => s.id === "sigkill");
const SIGKILL_COST = SIGNALS[SIGKILL_INDEX]?.cost ?? 5;

/** What the player did with the expensive signal. Three states, and the
 *  middle one is the whole point: PRIV sitting unspent in the bank is
 *  either a cost that reads as too dear or a verb nobody understood. */
export type SigkillUse = "unaffordable" | "afforded-unused" | "used";

/**
 * Watches a run and emits a small, fixed set of events.
 *
 * Created per run. `observe` is called with each tick's events; the
 * expensive-looking work in here is a handful of integer compares, and
 * it deliberately does no allocation in the common case.
 */
export type Telemetry = {
  observe: (events: readonly SimEvent[], state: SimState) => void;
  /** Called once when the run ends, or when the player leaves. Safe to
   *  call twice — the second is ignored, which is what makes the
   *  pagehide path safe. */
  finish: (outcome: RunOutcome, state: SimState) => void;
  started: () => boolean;
};

export function createTelemetry(opts: { nice: number; daily: boolean }): Telemetry {
  let begun = false;
  let done = false;
  let firstHitTick: number | null = null;
  let deepestPhase = 0;
  let currentCard: string | null = null;
  /** Phases already reported, so the funnel counts each once per run. */
  const phasesSeen = new Set<number>();

  // Split pace. `phase-clear` carries the *cumulative* tick, so the
  // segment is the difference — measuring from the start instead would
  // make every later runlevel look slower than it is.
  let lastPhaseTick = 0;

  // Economy. PRIV is only moved by things that also emit an event
  // (graze, successful signal, hit, reap), and `observe` is called on
  // exactly the ticks that produced events, so watching the balance
  // across observations is an exact ledger rather than a sample.
  let prevPriv: number | null = null;
  let privEarned = 0;
  let privSpent = 0;
  /** PRIV emptied by taking a hit — the tax, as distinct from spending. */
  let privWasted = 0;
  /** Grazes that arrived with the meter already full. Income thrown on
   *  the floor, and the clearest evidence that the cap is too low. */
  let privOverflow = 0;
  let sigkillAffordable = false;
  let sigkillFired = 0;

  return {
    started: () => begun,

    observe(events, state) {
      if (done) return;
      if (!begun && state.tick > 0) {
        begun = true;
        // Fires once per actual run. The gap between page views and
        // this number is the "did anyone manage to start it" metric —
        // exactly the signal that was missing when a dead overlay made
        // the game unstartable and every automated check still passed.
        track("daemon_start", { nice: opts.nice, daily: opts.daily });
        countSessionRun();
      }

      if (state.phaseIndex > deepestPhase) deepestPhase = state.phaseIndex;
      if (!phasesSeen.has(state.phaseIndex)) {
        phasesSeen.add(state.phaseIndex);
        track("daemon_phase", {
          phase: phaseName(state.phaseIndex),
          nice: opts.nice,
          at: bucketSeconds(state.tick),
        });
      }

      // Counted, not reported. Everything in this block is integer
      // arithmetic on a handful of locals; the only thing that reaches
      // the network is the one summary at `finish`.
      let grazes = 0;
      let spentThisTick = false;
      let hitThisTick = false;

      for (const e of events) {
        switch (e.t) {
          case "card-start":
            currentCard = e.name;
            break;
          case "card-clear":
            // Cleared vs timed out is the difference between a pattern
            // that is beaten and one that is merely survived.
            track("daemon_card", { card: e.name, nice: opts.nice, result: "cleared" });
            break;
          case "phase-clear": {
            // One event per runlevel, five per run at the very most.
            // This is the metric that shows a phase eating the clock —
            // a wall shows up as a loss, but a slog only shows up here.
            const segment = Math.max(0, e.ticks - lastPhaseTick);
            lastPhaseTick = e.ticks;
            track("daemon_split", {
              phase: e.phase,
              nice: opts.nice,
              daily: opts.daily,
              pace: bucketSeconds(segment),
            });
            break;
          }
          case "graze":
            grazes += 1;
            break;
          case "signal":
            if (e.ok) {
              spentThisTick = true;
              if (e.id === "sigkill") sigkillFired += 1;
            }
            break;
          case "player-hit":
            hitThisTick = true;
            if (firstHitTick === null) {
              firstHitTick = state.tick;
              // How long the opening keeps a newcomer alive. If this
              // clusters under ten seconds the tutorial is too hard.
              track("daemon_first_hit", { at: bucketSeconds(state.tick), nice: opts.nice });
            }
            break;
          default:
            break;
        }
      }

      // The ledger. A rise is income; a fall is either the price of a
      // signal or the tax on a hit, and telling those two apart is the
      // difference between "the cost is wrong" and "they keep dying".
      const priv = state.player.priv;
      if (prevPriv !== null) {
        const delta = priv - prevPriv;
        if (delta > 0) privEarned += delta;
        else if (delta < 0) {
          if (spentThisTick) privSpent += -delta;
          else if (hitThisTick) privWasted += -delta;
        }
      }
      // A graze on a full meter earns nothing. Attributed on the tick
      // rather than per-graze, so a flood costs one compare.
      if (grazes > 0 && priv >= state.player.privMax && (prevPriv === null || priv <= prevPriv)) {
        privOverflow += grazes;
      }
      prevPriv = priv;

      if (
        !sigkillAffordable &&
        priv >= SIGKILL_COST &&
        (state.player.cooldowns[SIGKILL_INDEX] ?? 0) === 0
      ) {
        sigkillAffordable = true;
      }
    },

    finish(outcome, state) {
      if (done) return;
      done = true;
      // A run nobody started is a page view, not a run. Reporting it
      // would quietly inflate every denominator.
      if (!begun) return;
      track(
        "daemon_run",
        runPayload({
          outcome,
          nice: opts.nice,
          daily: opts.daily,
          ticks: state.tick,
          phaseIndex: deepestPhase,
          cardId: currentCard,
          grazes: state.stats.grazes,
          hitsTaken: state.stats.hitsTaken,
          cardsCleared: state.stats.cardsCleared,
          signalsFired: state.stats.signalsFired,
          upgrades: state.upgrades,
          firstHitTick,
        }),
      );

      // The economy, once per run. `afforded-unused` is the row that
      // matters: it means somebody had the five PRIV, had the cooldown,
      // and still never pressed it — which is a pricing or legibility
      // problem, not a difficulty one, and nothing else on the
      // dashboard can tell those apart.
      track("daemon_economy", {
        outcome,
        nice: opts.nice,
        daily: opts.daily,
        earned: bucketCount(privEarned),
        spent: bucketCount(privSpent),
        spentPct: bucketRatio(privSpent, privEarned),
        wasted: bucketCount(privWasted),
        overflow: bucketCount(privOverflow),
        sigkill: (sigkillFired > 0
          ? "used"
          : sigkillAffordable
            ? "afforded-unused"
            : "unaffordable") satisfies SigkillUse,
      });

      if (outcome === "won") {
        // Which speedrun categories a win qualified for. Derived by
        // `records.ts`, never restated here — a second copy of the
        // qualification rules would drift, and then the dashboard would
        // disagree with the trophy the player was just shown.
        const cats = qualifyingCategories(
          resultFromState(state, {
            won: true,
            // Splits and offers do not affect qualification, and both
            // would be a fingerprint if they were sent.
            splits: [],
            offered: [],
            lastCard: currentCard,
          }),
        );
        track("daemon_category", {
          // Sorted by the fixed CATEGORIES order, so one set of
          // qualifications is always one row.
          categories: cats.join(","),
          count: cats.length,
          nice: opts.nice,
          daily: opts.daily,
          duration: bucketSeconds(state.tick),
        });
      }

      if (outcome === "lost") {
        // The tuning event. "Which named attack is killing people, at
        // which difficulty, how far in" is the whole question.
        track("daemon_death", {
          card: currentCard ?? "none",
          phase: phaseName(deepestPhase),
          nice: opts.nice,
          at: bucketSeconds(state.tick),
        });
      }
    },
  };
}

/** Draft telemetry. Offered-vs-picked is the only way to find a dead
 *  card: an upgrade with a low pick rate is either weak or unreadable,
 *  and the two look identical from the code. */
export function trackDraft(offers: readonly string[], picked: string | null, nice: number): void {
  track("daemon_draft", {
    picked: picked ?? "skipped",
    // Sorted so the same three offers always aggregate to one row
    // rather than six permutations.
    offered: [...offers].sort().join(","),
    nice,
  });
}

/** Fired when someone goes again. The "one more go" signal — the single
 *  best measure of whether the loop is actually fun. */
export function trackRestart(nice: number, previous: RunOutcome): void {
  track("daemon_restart", { nice, previous });
}

// ── Ghosts, replays and run codes ─────────────────────────────────────

/** What somebody did with the speedrunning apparatus. A closed set —
 *  every new verb has to be added here on purpose, which is the point. */
export type ReplayAction = "code-copy" | "code-import" | "ghost-race" | "replay-watch";

/** Where they did it from, so a dead entry point is distinguishable
 *  from a dead feature. */
export type ReplaySurface = "results" | "records" | "daily" | "link";

/**
 * Ghost / replay usage.
 *
 * The apparatus — run codes, ghosts, replays — is the most expensive
 * thing in the game per unit of screen, and the only way to know
 * whether it earns that is to count the times anyone touches it. If
 * these rows stay empty, the honest conclusion is to stop building on
 * top of it rather than to build more.
 */
export function trackReplayUse(
  action: ReplayAction,
  opts: { surface?: ReplaySurface; nice?: number } = {},
): void {
  track("daemon_replay", {
    action,
    surface: opts.surface ?? "results",
    // Optional: a run code copied from a link has no difficulty
    // attached, and inventing one would be worse than omitting it.
    nice: opts.nice ?? "unknown",
  });
}

// ── Daily engagement ──────────────────────────────────────────────────

/**
 * One finished attempt at the daily.
 *
 * Two questions, and they are different: does anyone *retry* the day's
 * seed once their recorded attempt is spent (attempts vs recorded), and
 * does anyone *come back tomorrow* (streak). A daily that gets one
 * attempt and no second day is a feature nobody wants; a daily that
 * gets six attempts and no second day is a feature that is too hard.
 */
export function trackDaily(o: {
  /** Attempts today including this one, recorded and practice. */
  attempt: number;
  /** Was this the one attempt that counts, or practice after it? */
  recorded: boolean;
  /** Consecutive days played, counting today. */
  streak: number;
  outcome: RunOutcome;
  nice: number;
}): void {
  track("daemon_daily", {
    attempts: bucketRuns(o.attempt),
    mode: o.recorded ? "recorded" : "practice",
    streak: bucketStreak(o.streak),
    outcome: o.outcome,
    nice: o.nice,
  });
}

// ── Difficulty movement ───────────────────────────────────────────────

/**
 * Someone changed `nice`.
 *
 * Both ends are sent raw because `NiceLevel` is eight values and always
 * will be — this is the one place a raw number is still low cardinality.
 * The direction is derived here rather than on the dashboard so that
 * "lower is meaner" lives in exactly one place; getting that backwards
 * in a chart is the kind of error nobody catches.
 *
 * A no-op change emits nothing. Opening the picker and closing it again
 * is not a decision.
 */
export function trackNiceChange(from: number, to: number): void {
  if (from === to) return;
  track("daemon_nice", { from, to, dir: to < from ? "harder" : "easier" });
}
